import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { CheckIcon, ChevronDownIcon } from "lucide-react";

import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  buildTimezoneSelectOptions,
  filterTimezoneSelectOptions,
  formatTimezoneSelectLabel,
  type TimezoneSelectOption,
} from "@/utils/ianaTimezones";
import {
  accountSettingsFieldSelectContentClassName,
  accountSettingsFieldSelectItemClassName,
  accountSettingsFieldSelectTriggerClassName,
} from "@/views/Dashboard/Profile/accountSettingsCardShell";

const TIMEZONE_OPTIONS = buildTimezoneSelectOptions();

export type TimezoneComboboxProps = {
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Extra classes for the trigger input (broker / CSV forms). */
  triggerClassName?: string;
  /** Extra classes for the dropdown panel. */
  contentClassName?: string;
  /** Extra classes for each option row. */
  itemClassName?: string;
  /** Optional prebuilt options (defaults to full IANA list with UTC offsets). */
  options?: TimezoneSelectOption[];
  /** Use account-settings select chrome (Display Preferences). */
  variant?: "default" | "account-settings";
  "aria-label"?: string;
};

/**
 * Select-looking combobox: click the field and type there; the menu filters in place.
 */
export function TimezoneCombobox({
  id,
  value,
  onValueChange,
  placeholder = "Select timezone",
  disabled = false,
  triggerClassName,
  contentClassName,
  itemClassName,
  options = TIMEZONE_OPTIONS,
  variant = "default",
  "aria-label": ariaLabel = "Timezone",
}: TimezoneComboboxProps): React.ReactElement {
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const blurCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);
  const isAccountSettings = variant === "account-settings";

  const selectedLabel = useMemo(() => {
    const trimmed = value.trim();
    if (!trimmed) return "";
    const match = options.find((option) => option.value === trimmed);
    if (match) return match.label;
    return formatTimezoneSelectLabel(trimmed, new Date());
  }, [options, value]);

  const filtered = useMemo(() => {
    const query =
      open && draft.trim() !== selectedLabel.trim() ? draft : "";
    return filterTimezoneSelectOptions(options, query);
  }, [draft, open, options, selectedLabel]);

  useEffect(() => {
    return () => {
      if (blurCloseTimerRef.current) clearTimeout(blurCloseTimerRef.current);
    };
  }, []);

  const syncMenuWidth = () => {
    const width = anchorRef.current?.getBoundingClientRect().width;
    if (width && width > 0) setMenuWidth(Math.round(width));
  };

  const openAndFocus = () => {
    if (disabled) return;
    if (blurCloseTimerRef.current) {
      clearTimeout(blurCloseTimerRef.current);
      blurCloseTimerRef.current = null;
    }
    syncMenuWidth();
    setDraft(selectedLabel);
    setOpen(true);
    requestAnimationFrame(() => {
      syncMenuWidth();
      const input = inputRef.current;
      if (!input) return;
      input.focus({ preventScroll: true });
      // Keep caret at end — full select() looks like a text field and fights the menu UX.
      const len = input.value.length;
      input.setSelectionRange(len, len);
    });
  };

  const commitOption = (option: TimezoneSelectOption) => {
    if (blurCloseTimerRef.current) {
      clearTimeout(blurCloseTimerRef.current);
      blurCloseTimerRef.current = null;
    }
    onValueChange(option.value);
    setDraft(option.label);
    setOpen(false);
    inputRef.current?.blur();
  };

  const closeWithoutCommit = () => {
    if (blurCloseTimerRef.current) {
      clearTimeout(blurCloseTimerRef.current);
      blurCloseTimerRef.current = null;
    }
    setOpen(false);
    setDraft(selectedLabel);
  };

  return (
    <Popover
      modal={false}
      open={open}
      onOpenChange={(next) => {
        if (disabled) return;
        if (next) {
          openAndFocus();
          return;
        }
        closeWithoutCommit();
      }}
    >
      <PopoverAnchor asChild>
        <div
          ref={anchorRef}
          data-field-select=""
          data-slot="select"
          className="relative w-full min-w-0"
        >
          <input
            ref={inputRef}
            id={id}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-label={ariaLabel}
            disabled={disabled}
            placeholder={placeholder}
            value={open ? draft : selectedLabel}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-1p-ignore=""
            data-lpignore="true"
            data-form-type="other"
            data-account-settings-field-select={isAccountSettings ? "" : undefined}
            data-timezone-combobox-trigger=""
            data-slot="timezone-combobox-trigger"
            className={cn(
              // Match shadcn Input (`h-8 px-2.5 py-1`), not SelectTrigger (`py-2` + flex/select-none).
              "box-border h-8 w-full max-w-full min-w-0 rounded-[6px] border border-input bg-transparent px-2.5 py-1 pr-8 text-sm outline-none",
              "font-[family-name:var(--font-primary)] font-normal normal-case tracking-normal",
              "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "dark:bg-input/30 dark:hover:bg-input/50",
              "placeholder:text-muted-foreground",
              isAccountSettings && accountSettingsFieldSelectTriggerClassName,
              isAccountSettings ? "text-gray-500 dark:text-white" : "text-gray-500",
              triggerClassName
            )}
            onFocus={() => {
              if (!open) openAndFocus();
            }}
            onClick={() => {
              if (!open) openAndFocus();
            }}
            onChange={(event) => {
              setDraft(event.target.value);
              if (!open) setOpen(true);
            }}
            onBlur={(event) => {
              const next = event.relatedTarget as Node | null;
              if (next && anchorRef.current?.contains(next)) return;
              const listbox = document.getElementById(listboxId);
              if (next && listbox?.contains(next)) return;
              blurCloseTimerRef.current = setTimeout(() => {
                closeWithoutCommit();
              }, 120);
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                closeWithoutCommit();
                inputRef.current?.blur();
                return;
              }
              if (event.key === "Enter") {
                event.preventDefault();
                if (filtered[0]) commitOption(filtered[0]);
                return;
              }
              if (event.key === "ArrowDown" && !open) {
                event.preventDefault();
                openAndFocus();
              }
            }}
          />
          <ChevronDownIcon
            className={cn(
              "pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2",
              isAccountSettings ? "text-gray-500 dark:text-white" : "text-muted-foreground"
            )}
            aria-hidden
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => {
          // Keep open when interacting with the typed field (anchor lives outside the portal).
          if (anchorRef.current?.contains(event.target as Node)) {
            event.preventDefault();
          }
        }}
        onFocusOutside={(event) => {
          if (anchorRef.current?.contains(event.target as Node)) {
            event.preventDefault();
          }
        }}
        className={cn(
          // Override Popover default `w-72` so the menu matches the field width.
          "z-[200] !max-w-none p-1 shadow-none",
          menuWidth ? "!w-[var(--tz-menu-width)]" : "w-full",
          "rounded-[6px] border border-solid border-input bg-popover",
          "font-[family-name:var(--font-primary)] text-sm font-normal normal-case tracking-normal",
          isAccountSettings && accountSettingsFieldSelectContentClassName,
          isAccountSettings ? "text-gray-500 dark:text-white" : "text-gray-500",
          contentClassName
        )}
        data-timezone-combobox-content=""
        style={
          menuWidth
            ? ({
                ["--tz-menu-width" as string]: `${menuWidth}px`,
                width: menuWidth,
                minWidth: menuWidth,
                maxWidth: menuWidth,
              } as React.CSSProperties)
            : undefined
        }
      >
        <div
          id={listboxId}
          role="listbox"
          aria-label="Timezones"
          className="max-h-60 overflow-y-auto overscroll-contain"
        >
          {filtered.length === 0 ? (
            <p className="px-2 py-2.5 font-[family-name:var(--font-primary)] text-sm text-muted-foreground">
              No matching timezones
            </p>
          ) : (
            filtered.map((option) => {
              const selected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={cn(
                    "relative flex w-full cursor-default items-center gap-3 rounded-[4px] px-2 py-1.5 text-left text-sm outline-hidden select-none",
                    "font-[family-name:var(--font-primary)] font-normal normal-case tracking-normal",
                    "hover:bg-accent focus:bg-accent focus-visible:bg-accent",
                    selected && "bg-accent/60",
                    isAccountSettings && accountSettingsFieldSelectItemClassName,
                    itemClassName
                  )}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    if (blurCloseTimerRef.current) {
                      clearTimeout(blurCloseTimerRef.current);
                      blurCloseTimerRef.current = null;
                    }
                    commitOption(option);
                  }}
                >
                  <span className="w-[5.75rem] shrink-0 tabular-nums text-muted-foreground">
                    {option.offsetLabel}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{option.placeLabel}</span>
                  {selected ? (
                    <CheckIcon
                      className="size-3.5 shrink-0 text-[color:var(--brand-tropical-surf)]"
                      aria-hidden
                    />
                  ) : (
                    <span className="size-3.5 shrink-0" aria-hidden />
                  )}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
